import { useRef, useState } from 'react'

import styled from 'styled-components'

import { generateInspectionXRay } from '../../../../api/inspectionTools'
import { useNotifications } from '../../../../contexts/notificationProvider'
import { AddProjectButton } from '../../buildings_page/components/AddProjectButton'

const Section = styled.div`
  background: #ffffff;
  border: 1px solid #e6e8ec;
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 20px;
`

const SectionTitle = styled.h2`
  margin: 0 0 12px;
`

const SectionSubtitle = styled.p`
  margin: 0 0 20px;
  color: #666;
`

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
`

const Label = styled.label`
  display: block;
  font-size: 12px;
  color: #4a4a4a;
  margin-bottom: 8px;
`

const FileInput = styled.input`
  display: none;
`

const UploadDropZone = styled.button<{ hasFile: boolean }>`
  width: 100%;
  min-height: 124px;
  border: 2px dashed ${props => (props.hasFile ? '#0f7a43' : '#c9d2de')};
  background: ${props => (props.hasFile ? '#ecfff3' : '#f8fafc')};
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 16px;
  color: #3e4a59;
`

const UploadFileName = styled.div`
  margin-top: 8px;
  padding: 8px 10px;
  border: 1px solid #9fd8b8;
  background: #ffffff;
  border-radius: 6px;
  font-weight: 600;
  color: #1a3b2a;
`

const ActionsRow = styled.div`
  margin-top: 20px;
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`

const PreviewImage = styled.img`
  width: 100%;
  max-width: 860px;
  border: 1px solid #d5d7dc;
  border-radius: 6px;
`

export const XRayGeneratorTool = () => {
  const [rgbFile, setRgbFile] = useState<File | null>(null)
  const [uvFile, setUvFile] = useState<File | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [xrayBase64, setXrayBase64] = useState('')

  const rgbInputRef = useRef<HTMLInputElement | null>(null)
  const uvInputRef = useRef<HTMLInputElement | null>(null)
  const { addNotification } = useNotifications()

  const imageHref = xrayBase64 ? `data:image/jpeg;base64,${xrayBase64}` : ''

  const onGenerate = async () => {
    if (!rgbFile || !uvFile) {
      addNotification('Please upload both RGB and UV images', 'warning')
      return
    }

    setIsLoading(true)
    try {
      const response = await generateInspectionXRay({ rgbFile, uvFile })
      setXrayBase64(response.xray_image_base64)
      addNotification('XRay image generated', 'success')
    } catch (error) {
      addNotification('Unable to generate XRay image', 'error')
    } finally {
      setIsLoading(false)
    }
  }

  const onDownload = () => {
    if (!imageHref) return
    const anchor = document.createElement('a')
    anchor.href = imageHref
    anchor.download = 'inspection-tools-xray.jpg'
    anchor.click()
  }

  return (
    <>
      <Section>
        <SectionTitle>XRay Generator</SectionTitle>
        <SectionSubtitle>
          Upload matching RGB and UV photos from the same viewpoint to generate
          an XRay diff image.
        </SectionSubtitle>

        <Grid>
          <div>
            <Label htmlFor="xray-rgb-upload">RGB Image</Label>
            <UploadDropZone
              type="button"
              hasFile={Boolean(rgbFile)}
              onClick={() => rgbInputRef.current?.click()}
            >
              <div>
                {rgbFile ? 'RGB image selected' : 'Click to upload RGB image'}
                {rgbFile ? (
                  <UploadFileName>{rgbFile.name}</UploadFileName>
                ) : null}
              </div>
            </UploadDropZone>
            <FileInput
              ref={rgbInputRef}
              id="xray-rgb-upload"
              type="file"
              accept="image/*"
              onChange={event => setRgbFile(event.target.files?.[0] ?? null)}
            />
          </div>
          <div>
            <Label htmlFor="xray-uv-upload">UV Image</Label>
            <UploadDropZone
              type="button"
              hasFile={Boolean(uvFile)}
              onClick={() => uvInputRef.current?.click()}
            >
              <div>
                {uvFile ? 'UV image selected' : 'Click to upload UV image'}
                {uvFile ? <UploadFileName>{uvFile.name}</UploadFileName> : null}
              </div>
            </UploadDropZone>
            <FileInput
              ref={uvInputRef}
              id="xray-uv-upload"
              type="file"
              accept="image/*"
              onChange={event => setUvFile(event.target.files?.[0] ?? null)}
            />
          </div>
        </Grid>

        <ActionsRow>
          <AddProjectButton
            text={isLoading ? 'Generating...' : 'Generate XRay'}
            onClick={onGenerate}
          />
          {xrayBase64 ? (
            <AddProjectButton text="Download XRay" onClick={onDownload} />
          ) : null}
        </ActionsRow>
      </Section>

      {xrayBase64 ? (
        <Section>
          <SectionTitle as="h3">XRay Preview</SectionTitle>
          <PreviewImage src={imageHref} alt="Generated xray preview" />
        </Section>
      ) : null}
    </>
  )
}
